<script>
  import {
    Tabs,
    TabsItem,
    Breadcrumb,
    BreadcrumbItem,
    Loader,
    Flex,
    Badge,
    Tag,
    FlexItem,
    Page,
  } from "@gl/design-system";
  import { Route, Router } from "svelte-routing";
  import { navigate } from "../../utils/index";
  import { Layout } from "../../components/index";
  import { onMount, getContext } from "svelte";
  import { _, contract } from "../../stores/index";
  import { UsersRepository } from "../../repositories/index";
  import UserData from "./UserData.svelte";
  import UserKey from "./UserKey.svelte";
  import UserPolicies from "./UserPolicies.svelte";
  const { isAuthorized } = getContext("isAuthorized");

  export let id;
  export let user;

  const basepath = "users";
  let loading = true;
  let selectedTab = "data";

  const setSelectedTabFromPathname = () => {
    const path = decodeURIComponent(window.location.pathname);

    if (path !== basepath) {
      if (path.includes("data")) {
        selectedTab = "data";
      } else if (path.includes("policies")) {
        selectedTab = "policies";
      } else {
        selectedTab = "api";
      }
    } else {
      selectedTab = "data";
    }
  };

  async function fetchUser() {
    try {
      loading = true;
      user = await UsersRepository.getUser(id);

      const groups = await UsersRepository.getUserGroups(id);
      user.groups = groups.list;

      const roles = await UsersRepository.getUserRoles(id);
      user.roles = roles.list;

      const apps = await UsersRepository.getUserApps(id);
      user.apps = apps.list;

      return user;
    } finally {
      loading = false;
    }
  }

  onMount(() => {
    fetchUser();
    setSelectedTabFromPathname();
  });
</script>

<Layout selected="users" fillHeight>
  {#if !loading}
    <Page>
      <Breadcrumb slot="breadcrumb">
        <BreadcrumbItem
          on:click={() => navigate("/users")}
          on:keypress={(e) => {
            if (e.key === "Enter") navigate("/users");
          }}>{$_("common.users")}</BreadcrumbItem
        >
        <BreadcrumbItem current>
          {$_("common.user")}: {user.data.firstName}
          {user.data.lastName}</BreadcrumbItem
        >
      </Breadcrumb>

      <Flex slot="header" class="align-items-center">
        <h3 class="gl-title level-3">
          {$_("common.user")}: {user.data.firstName}
        </h3>
        {#if user.data.isServiceAccount}
          <Tag status="default" class="ml-2" style="font-size: 12px;">
            {$_("users.service_account")}
          </Tag>
        {:else}
          <h3 class="gl-title level-3">
            {user.data.lastName}
          </h3>
        {/if}

        <div
          class="active-badge ml-4"
          class:background-color-success={user.data.active}
          class:background-color-error={!user.data.active}
        >
          {user.data.active ? $_("common.active") : $_("common.inactive")}
        </div>
      </Flex>

      <Tabs slot="tabs">
        <TabsItem
          selected={selectedTab === "data"}
          on:click={() => {
            selectedTab = "data";
            navigate(`/users/${id}/data`);
          }}
        >
          {$_("users.data")}
        </TabsItem>
        <TabsItem
          selected={selectedTab === "api"}
          on:click={() => {
            selectedTab = "api";
            navigate(`/users/${id}/api`);
          }}
        >
          {$_("users.api.keys")}
        </TabsItem>
        {#if $isAuthorized("policy_read") && $isAuthorized("group_read") && $isAuthorized("role_read")}
          <TabsItem
            selected={selectedTab === "policies"}
            on:click={() => {
              selectedTab = "policies";
              navigate(`/users/${id}/policies`);
            }}
          >
            {$_("common.policies")}
          </TabsItem>
        {/if}
      </Tabs>

      <div class="tab-content">
        <Router {basepath}>
          <Route path="/data">
            <UserData {user} on:change={() => fetchUser()} />
          </Route>
          <Route path="/policies">
            <UserPolicies {id} {user} on:save={() => fetchUser()} />
          </Route>
          <Route path="/api">
            <UserKey {id} {user} />
          </Route>
        </Router>
      </div>
    </Page>
  {:else}
    <Loader />
  {/if}
</Layout>

<style>
  .active-badge {
    padding: 6px 4px;
    height: 24px;
    font-size: 11px;
    text-transform: uppercase;
    color: var(--gl-text-invert);
    font-weight: 700;
    border-radius: 4px;
    display: flex;
    align-items: center;
  }
</style>
